import { AxiosError } from 'axios';
import { createContext, useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import {
  IReviewSummaryReport,
  useGetReviewSummaryReport,
} from '@Domains/ReviewSummaryReport';
import { useProfile } from 'src/Constant/UserProfileContext/UserProfileProvider';

type ReviewSummaryReportProviderProps = {
  children: React.ReactNode;
};

type ReviewSummaryReportContextProps = {
  summaryData: IReviewSummaryReport | undefined;
  loading: boolean;
  error?: AxiosError | null;
  triggerRefetch?: () => void;
};

const ReviewSummaryReportContext = createContext<
  ReviewSummaryReportContextProps | undefined
>({ summaryData: undefined, loading: false });

const ReviewSummaryReportProvider = (
  props: ReviewSummaryReportProviderProps
) => {
  const { id } = useParams();
  const { user } = useProfile();
  const companyId = user?.I_COMPANY_ID?.toString() || '';

  const [{ data: summaryData, loading, error }, refetch] =
    useGetReviewSummaryReport(id, companyId);

  const [trigger, setTrigger] = useState<boolean>(false);
  const triggerRefetch = () => setTrigger(true);

  useEffect(() => {
    if (trigger) {
      refetch();
      setTrigger(false);
    }
  }, [trigger]);

  return (
    <ReviewSummaryReportContext.Provider
      value={{ summaryData, loading, error, triggerRefetch }}
    >
      {props.children}
    </ReviewSummaryReportContext.Provider>
  );
};

const useReviewSummaryReport = () => {
  const context = useContext(ReviewSummaryReportContext);

  if (!context) {
    throw new Error(
      'useReviewSummaryReport must be used within a ReviewSummaryContextProvider'
    );
  }

  return context;
};

export { useReviewSummaryReport };
export default ReviewSummaryReportProvider;
